import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { NavigationParamKeys } from '@/types/queryParamKeys';
import {
  Chip,
  ColorsV2,
  Text,
  Tab,
  TabProps,
  spaces,
  useBreakpoint,
  useTabContext,
  useThemeColors,
} from '@electricjs/arc';

export const StyledTab = ({
  children,
  id,
  onClick,
  count,
  ...otherProps
}: TabProps & { count?: number }) => {
  const { selectedId } = useTabContext();
  const { md } = useBreakpoint();
  const { space_xs } = spaces;
  const [searchParams, setQueryParams] = useSearchParams();
  const isActive = selectedId === id;
  const [activeTabChipBgColor, inactiveTabChipBgColor] = useThemeColors([
    ColorsV2.PRIMARY,
    ColorsV2.GRAY_LIGHT,
  ]);

  const handleTabChange = useCallback(
    (tab: string) => {
      // Only update query params if the tab is actually different
      const currentTab = searchParams.get(NavigationParamKeys.Tab);
      if (currentTab !== tab) {
        setQueryParams(
          prev => ({
            ...Object.fromEntries(prev),
            [NavigationParamKeys.Tab]: tab,
          }),
          { replace: true }
        );
      }

      if (onClick) {
        onClick();
      }
    },
    [searchParams, setQueryParams, onClick]
  );

  return (
    <Tab
      id={id}
      selectedColor={ColorsV2.PRIMARY_LIGHT}
      width={md ? '180px' : 'auto'}
      {...otherProps}
      onClick={() => handleTabChange(id ?? '')}>
      <Text variant={selectedId === id ? 'label-large' : 'body'}>
        {children}
      </Text>
      {count !== undefined && count !== null && (
        <Chip
          id={`${id}-count-chip`}
          ml={space_xs}
          backgroundColor={
            isActive ? activeTabChipBgColor : inactiveTabChipBgColor
          }
          variant="small">
          <Text
            variant="legal"
            intent={isActive ? 'primary-invert' : 'secondary'}
            fontWeight="bold">
            {count}
          </Text>
        </Chip>
      )}
    </Tab>
  );
};
